import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { ref, computed } from 'vue';


const __default__ = { name: 'ReportFilterDate' }


export default /*@__PURE__*/_defineComponent({
  ...__default__,
  props: {
    data: {},
    loader: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

/* ====== Dark mode ====== */
const isDarkMode = ref(
  typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false
);
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', e => (isDarkMode.value = e.matches));
}

/* ====== Branding por hostname ====== */
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isDominiomedios = hostname === 'crm.dominiomedios.com';

// Paleta Dominiomedios (rojos)
const DOMINIO_MEDIOS_PALETTE = [
  '#be103b', '#88102e', '#6e0f26', '#a1293a', '#944249', '#b5696d', '#be103b'
];

// Base azul para otros clientes
const BLUE_BASE = '#2563eb';

// Helpers para generar escala
function hexToHsl(hex: string) {
  let r = 0, g = 0, b = 0;
  const c = hex.replace('#','');
  if (c.length === 3) {
    r = parseInt(c[0] + c[0], 16);
    g = parseInt(c[1] + c[1], 16);
    b = parseInt(c[2] + c[2], 16);
  } else {
    r = parseInt(c.substring(0,2), 16);
    g = parseInt(c.substring(2,4), 16);
    b = parseInt(c.substring(4,6), 16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  let h = 0, s = 0, l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h, s, l };
}
function hslToHex(h: number, s: number, l: number) {
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
function buildScale(baseHex: string, n: number) {
  const { h, s } = hexToHsl(baseHex);
  const colors: string[] = [];
  const lMin = 0.30, lMax = 0.72; // oscuro → claro
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0.5 : i / (n - 1);
    const li = lMin + (lMax - lMin) * (1 - t);
    const si = Math.min(1, Math.max(0.35, s * (0.85 + 0.2 * (1 - t))));
    colors.push(hslToHex(h, si, li));
  }
  return colors;
}
function repeatPalette(palette: string[], n: number) {
  if (n <= palette.length) return palette.slice(0, n);
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(palette[i % palette.length]);
  return out;
}

/* ====== Datos ====== */
const reportLoader = computed(() => props.loader ?? false);

const chartSeries = computed(() => [
  {
    name: 'Ventas',
    data: props.data.map(user => parseFloat(user.ventas) || 0),
  },
]);

// Paleta final para ApexCharts
const chartColors = computed(() => {
  const n = Math.max(1, props.data.length);
  if (isDominiomedios) {
    return repeatPalette(DOMINIO_MEDIOS_PALETTE, n);
  }
  return buildScale(BLUE_BASE, n);
});

/* ====== Opciones Apex ====== */
const chartOptions = computed(() => ({
  chart: {
    type: 'bar',
    foreColor: isDarkMode.value ? '#fff' : '#333',
    toolbar: { show: false },
  },
  plotOptions: {
    bar: {
      dataLabels: { orientation: 'vertical' },
      distributed: true,
      columnWidth: '40%',
      borderRadius: 4,
    },
  },
  dataLabels: {
    enabled: true,
    offsetX: 0,
    offsetY: 20,
    style: {
      colors: [isDarkMode.value ? '#fff' : '#333'],
      fontWeight: 'bold',
      textShadow: '2px 2px 2px rgba(0,0,0,0.25)'
    },
    formatter: (val: number, opt: any) => {
      const index = opt.dataPointIndex;
      const soldUser = props.data[index];
      return (parseFloat(soldUser?.total_vendido ?? 0) || 0)
        .toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    },
  },
  xaxis: {
    categories: props.data.map(user => user.vendedor ?? 'Sin nombre'),
    labels: {
      rotate: -40,
      style: {
        colors: isDarkMode.value ? '#ccc' : '#666',
        fontSize: '14px',
      },
    },
  },
  yaxis: {
    min: 0,
    forceNiceScale: true,
    labels: {
      formatter: (val: number) => (Number.isInteger(val) ? val : ''),
      style: {
        colors: isDarkMode.value ? '#ccc' : '#666',
        fontSize: '14px',
      },
    },
  },
  tooltip: {
    theme: isDarkMode.value ? 'dark' : 'light',
    y: {
      formatter: (_val: number, { dataPointIndex }: any) =>
        (parseFloat(props.data[dataPointIndex]?.total_vendido ?? 0) || 0)
          .toLocaleString('es-MX', { style: 'currency', currency: 'MXN' }),
    },
  },
  colors: chartColors.value,  // ← paleta dinámica
  legend: { show: false },
  grid: {
    borderColor: isDarkMode.value ? '#444' : '#eee',
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
  },
}));

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_apexchart = _resolveComponent("apexchart")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createBlock(_component_v_card, {
    flat: "",
    class: "px-8 pt-6"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card_title, null, {
        default: _withCtx(() => _cache[0] || (_cache[0] = [
          _createTextVNode("Ventas por Usuario")
        ])),
        _: 1,
        __: [0]
      }),
      (reportLoader.value)
        ? (_openBlock(), _createBlock(_component_v_progress_linear, {
            key: 0,
            indeterminate: "",
            color: "primary"
          }))
        : (_openBlock(), _createBlock(_component_apexchart, {
            key: 1,
            type: "bar",
            height: "400",
            options: chartOptions.value,
            series: chartSeries.value
          }, null, 8, ["options", "series"]))
    ]),
    _: 1
  }))
}
}

})